import apiClient from '../utils/api';

export interface SectionLocale {
  en?: { name?: string };
  ar?: { name?: string };
}

export interface ApiSection {
  sectionId: string;
  name: string;
  displayOrder: string;
  vendorId: string;
  createdAt: string;
  updatedAt: string;
  locale?: SectionLocale;
}

export interface SectionsApiResponse {
  success: boolean;
  message?: string;
  sections: ApiSection[];
}

// Get all sections for a vendor
export const getSections = async (vendorId: string): Promise<SectionsApiResponse> => {
  const url = `/vendors/${vendorId}/sections`;
  
  const response = await apiClient.get<{ message?: string; data: { sections: ApiSection[] } }>(url);
  return {
    success: true,
    message: response.data.message,
    sections: response.data.data?.sections ?? [],
  };
};

export interface CreateSectionRequest {
  name: string;
  displayOrder: number;
  locale?: SectionLocale;
}

export interface CreateSectionResponse {
  success: boolean;
  message?: string;
  sectionId?: string;
}

export interface UpdateSectionRequest {
  name?: string;
  displayOrder?: number;
  locale?: SectionLocale;
}

export interface UpdateSectionResponse {
  success: boolean;
  message?: string;
  section?: ApiSection;
}

// Create a new section for a vendor
export const createSection = async (
  vendorId: string,
  body: CreateSectionRequest
): Promise<CreateSectionResponse> => {
  const url = `/vendors/${vendorId}/sections`;

  const response = await apiClient.post<{ message?: string; data: { sectionId: string } }>(url, {
    name: body.name,
    displayOrder: body.displayOrder,
    ...(body.locale && Object.keys(body.locale).length > 0 && { locale: body.locale }),
  });

  return {
    success: true,
    message: response.data.message,
    sectionId: response.data.data?.sectionId,
  };
};

// Update an existing section
export const updateSection = async (
  vendorId: string,
  sectionId: string,
  body: UpdateSectionRequest
): Promise<UpdateSectionResponse> => {
  const url = `/vendors/${vendorId}/sections/${sectionId}`;

  const payload: Record<string, unknown> = {};
  if (body.name !== undefined) payload.name = body.name;
  if (body.displayOrder !== undefined) payload.displayOrder = body.displayOrder;
  if (body.locale !== undefined && Object.keys(body.locale).length > 0) payload.locale = body.locale;

  const response = await apiClient.put<{ message?: string; data: { section: ApiSection } }>(
    url,
    payload
  );

  return {
    success: true,
    message: response.data.message,
    section: response.data.data?.section,
  };
};

// Delete a section
export const deleteSection = async (sectionId: string): Promise<any> => {
  const response = await apiClient.delete(`/vendors/sections/${sectionId}`);
  return response.data;
};

